import { z } from 'zod';
import { uuidSchema as uuid } from './schemas';

/**
 * Bons d'enlèvement chez le fournisseur. Émis par le planificateur pour un chantier, consultables et téléchargeables
 * par le technicien. Infalsifiables : numéro unique, code de vérification aléatoire, QR code vers la page publique
 * de vérification (contenu officiel et statut), contenu figé en base, usage unique.
 */
export const SUPPLIER = 'supplier';
export const PICKUP_STATUSES = ['issued', 'picked_up', 'cancelled'] as const;
export type PickupStatus = (typeof PICKUP_STATUSES)[number];
export const PICKUP_STATUS_LABEL: Record<PickupStatus, string> = {
  issued: 'Valable',
  picked_up: 'Matériel récupéré',
  cancelled: 'Annulé',
};
export const PICKUP_VALID_DAYS_DEFAULT = 30;
/** code de vérification : sans caractères ambigus (0/O, 1/I/L) pour être dicté ou recopié sans erreur */
export const PICKUP_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const PICKUP_CODE_LENGTH = 8;
export const PICKUP_VERIFY_PATH = '/verifier-bon';

/**
 * Réglage de l'entreprise : vérification du bon par le fournisseur (QR code, code, page publique). Désactivée par
 * défaut (à activer une fois les fournisseurs prévenus). Le reste (numéro, contenu figé, usage unique) s'applique
 * toujours.
 */
export const pickupSettingsSchema = z.object({ supplierVerification: z.boolean().default(false) });
export type PickupSettings = z.infer<typeof pickupSettingsSchema>;
export function pickupSettingsOf(settings: unknown): PickupSettings {
  const parsed = pickupSettingsSchema.safeParse((settings as { pickupOrders?: unknown } | null | undefined)?.pickupOrders ?? {});
  return parsed.success ? parsed.data : { supplierVerification: false };
}

export const pickupLineSchema = z.object({
  label: z.string().trim().min(1, 'Désignation obligatoire').max(200),
  quantity: z.number().positive('Quantité invalide').max(999),
});

export const pickupOrderInput = z.object({
  id: uuid,
  supplierId: uuid,
  technicianId: uuid.nullish().transform((v) => v ?? null),
  lines: z.array(pickupLineSchema).min(1, 'Ajoutez au moins un article à enlever').max(40),
  notes: z
    .string()
    .trim()
    .max(1000)
    .nullish()
    .transform((v) => v || null),
  validDays: z.number().int().min(1).max(180).default(PICKUP_VALID_DAYS_DEFAULT),
});
export type PickupOrderInput = z.input<typeof pickupOrderInput>;

export const pickupCancelInput = z.object({ reason: z.string().trim().min(3, 'Motif obligatoire').max(500) });

/** saisie sur la page publique : majuscules, espaces et tirets du code ignorés */
export const pickupVerifyInput = z.object({
  e: z.string().trim().min(1).max(80),
  n: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^BE-\d{4}-\d{5,}$/, 'Numéro de bon invalide'),
  c: z
    .string()
    .toUpperCase()
    .transform((s) => s.replace(/[\s-]/g, ''))
    .pipe(z.string().length(PICKUP_CODE_LENGTH, 'Code invalide')),
});

export interface PickupOrderRecord {
  id: string;
  dossierId: string;
  dossierReference: string | null;
  number: string;
  verifyCode: string;
  supplierId: string;
  supplierName: string;
  technicianId: string | null;
  technicianName: string | null;
  clientLabel: string;
  siteCity: string | null;
  lines: { label: string; quantity: number }[];
  notes: string | null;
  validUntil: string;
  status: PickupStatus;
  /** valable mais date dépassée */
  expired: boolean;
  fileId: string | null;
  contentHash: string;
  /** adresse de vérification, relative à l'application Web (celle du QR code) ; null = vérification désactivée */
  verifyPath: string | null;
  issuedAt: string;
  issuedByName: string;
  pickedUpAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
}

/** réponse de la page publique de vérification (client masqué : initiale du nom + commune) */
export interface PickupVerification {
  company: string;
  number: string;
  status: PickupStatus;
  expired: boolean;
  valid: boolean;
  supplier: string;
  technician: string | null;
  client: string;
  siteCity: string | null;
  lines: { label: string; quantity: number }[];
  notes: string | null;
  issuedAt: string;
  validUntil: string;
  pickedUpAt: string | null;
  cancelledAt: string | null;
  contentHash: string;
}

/** contenu canonique (ordre fixe) dont l'empreinte SHA-256 figure sur le PDF */
export function pickupCanonical(o: {
  company: string;
  number: string;
  supplier: string;
  technician: string | null;
  client: string;
  siteCity: string | null;
  dossierReference: string | null;
  lines: { label: string; quantity: number }[];
  notes: string | null;
  issuedAt: string;
  validUntil: string;
}): string {
  return JSON.stringify([
    'pickup-order/v1',
    o.company,
    o.number,
    o.supplier,
    o.technician,
    o.client,
    o.siteCity,
    o.dossierReference,
    o.lines.map((l) => [l.label, l.quantity]),
    o.notes,
    o.issuedAt,
    o.validUntil,
  ]);
}

/** « Jean Dupont » → « J. D*** » ; société : nom complet (déjà public) */
export function maskClientName(label: string, isCompany: boolean): string {
  if (isCompany) return label;
  const parts = label.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '—';
  return parts.map((p, i) => (i === 0 ? `${p[0]!.toUpperCase()}.` : `${p[0]!.toUpperCase()}***`)).join(' ');
}

/** dernier jour de validité (inclus), dans le fuseau de l'entreprise */
export function pickupValidUntil(issuedAt: Date, days: number, timeZone: string): string {
  const d = new Date(issuedAt.getTime() + days * 86_400_000);
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}

export function pickupExpired(validUntil: string, now: Date, timeZone: string): boolean {
  const today = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
  return today > validUntil;
}

/** message à envoyer au fournisseur (WhatsApp, SMS…) : contenu + lien de vérification (celui du QR code) */
export function pickupShareText(o: Pick<PickupOrderRecord, 'number' | 'supplierName' | 'technicianName' | 'lines' | 'verifyPath' | 'verifyCode'>, webUrl: string): string {
  return [
    `Bon d'enlèvement ${o.number} — ${o.supplierName}`,
    ...o.lines.map((l) => `• ${l.quantity} × ${l.label}`),
    o.technicianName ? `Enlevé par : ${o.technicianName}` : null,
    o.verifyPath ? `Code de vérification : ${o.verifyCode}` : null,
    o.verifyPath ? `Vérifiez le bon avant de remettre le matériel : ${webUrl.replace(/\/$/, '')}${o.verifyPath}` : null,
  ]
    .filter(Boolean)
    .join('\n');
}
